import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';
import Sidebar from './components/Sidebar';
import AdminSidebar from './components/AdminSidebar';
import Login from './pages/Login';
import PayIN from './pages/PayIN';
import PayOUT from './pages/PayOUT';
import Requisites from './pages/Requisites';
import Devices from './pages/Devices';
import Messages from './pages/Messages';
import Wallet from './pages/Wallet';
import AdminPayIN from './pages/admin/AdminPayIN';
import AdminPayOUT from './pages/admin/AdminPayOUT';
import AdminRequisites from './pages/admin/AdminRequisites';
import AdminDevices from './pages/admin/AdminDevices';
import AdminGateways from './pages/admin/AdminGateways';
import AdminPrices from './pages/admin/AdminPrices';
import AdminBanks from './pages/admin/AdminBanks';
import AdminCurrencies from './pages/admin/AdminCurrencies';
import AdminMessages from './pages/admin/AdminMessages';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminRequests from './pages/admin/AdminRequests';
import BalanceOperations from './pages/admin/BalanceOperations';
import AdminDashboard from './pages/admin/AdminDashboard';
import LiveDashboard from './pages/admin/LiveDashboard';
import TraderDashboard from './pages/TraderDashboard';
import Traders from './pages/admin/Traders';
import CorezoidPanel from './components/CorezoidPanel';
import BalanceBar from './components/BalanceBar';

// Component for protecting routes
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Component for protecting admin routes
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || (user.role !== 'admin' && !user.isAdmin)) {
    return <Navigate to="/payin" replace />;
  }

  return children;
};

const CorezoidRoute = () => {
  const { user } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || user.isAdmin);

  if (isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user?.role !== 'trader') {
    return <Navigate to="/payin" replace />;
  }

  return (
    <>
      <Sidebar />
      <div className="App-content">
        <div className="corezoid-page">
          <div className="page-header">
            <div className="page-heading">
              <h1>API Console</h1>
              <p>Send operational tasks through the protected Corezoid console.</p>
            </div>
          </div>
          <div className="page-content">
            <CorezoidPanel />
          </div>
        </div>
      </div>
    </>
  );
};

const TraderLayout = ({ children }) => (
  <>
    <Sidebar />
    <div className="App-content trader-app-content">
      <div className="trader-balance-floating">
        <BalanceBar compact />
      </div>
      {children}
    </div>
  </>
);

const AdminLayout = ({ children }) => (
  <>
    <AdminSidebar />
    <div className="App-content">{children}</div>
  </>
);

const RoleRoute = ({ trader, admin, supportAllowed = false, gatewayAllowed = true }) => {
  const { user } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || user.isAdmin);

  if (user?.role === 'support' && !supportAllowed) {
    return <Navigate to="/payin" replace />;
  }

  if (user?.role === 'gateway' && !gatewayAllowed) {
    return <Navigate to="/payin" replace />;
  }

  return isAdmin || user?.role === 'support'
    ? <AdminLayout>{admin}</AdminLayout>
    : <TraderLayout>{trader}</TraderLayout>;
};

const HomeRoute = () => {
  return <Navigate to="/dashboard" replace />;
};

const DashboardRoute = () => {
  const { user } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || user.isAdmin);

  if (isAdmin) {
    return <AdminLayout><AdminDashboard /></AdminLayout>;
  }

  if (user?.role === 'support') {
    return <Navigate to="/payin" replace />;
  }

  return <TraderLayout><TraderDashboard /></TraderLayout>;
};

const LegacyAdminRedirect = () => {
  const { section } = useParams();
  return <Navigate to={`/${section}`} replace />;
};

const pageTitles = {
  '/login': 'Login',
  '/dashboard': 'Dashboard',
  '/dashboard/live': 'Live Operations Dashboard',
  '/payin': 'PayIN',
  '/payout': 'PayOUT',
  '/requisites': 'Requisites',
  '/devices': 'Devices',
  '/gateways': 'Gateways',
  '/traders': 'Traders',
  '/prices': 'Prices',
  '/banks': 'Banks',
  '/currencies': 'Currencies',
  '/messages': 'Messages',
  '/transactions': 'Transactions',
  '/requests': 'Requests',
  '/balance-operations': 'Balance Operations',
  '/wallet': 'Wallet',
  '/corezoid': 'API Console'
};

const RouteTitle = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = pageTitles[pathname] || 'NadalPay Traders';
  }, [pathname]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <RouteTitle />
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <HomeRoute />
              </ProtectedRoute>
            } />
            <Route path="/payin" element={
              <ProtectedRoute>
                <RoleRoute trader={<PayIN />} admin={<AdminPayIN />} supportAllowed />
              </ProtectedRoute>
            } />
            <Route path="/payout" element={
              <ProtectedRoute>
                <RoleRoute trader={<PayOUT />} admin={<AdminPayOUT />} supportAllowed />
              </ProtectedRoute>
            } />
            <Route path="/requisites" element={
              <ProtectedRoute>
                <RoleRoute trader={<Requisites />} admin={<AdminRequisites />} gatewayAllowed={false} />
              </ProtectedRoute>
            } />
            <Route path="/devices" element={
              <ProtectedRoute>
                <RoleRoute trader={<Devices />} admin={<AdminDevices />} gatewayAllowed={false} />
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <ProtectedRoute>
                <RoleRoute trader={<Messages />} admin={<AdminMessages />} gatewayAllowed={false} />
              </ProtectedRoute>
            } />
            <Route path="/transactions" element={
              <ProtectedRoute>
                <RoleRoute trader={<Navigate to="/wallet" replace />} admin={<AdminTransactions />} gatewayAllowed={false} />
              </ProtectedRoute>
            } />
            <Route path="/wallet" element={
              <ProtectedRoute>
                <RoleRoute trader={<Wallet />} admin={<Navigate to="/dashboard" replace />} gatewayAllowed={false} />
              </ProtectedRoute>
            } />
            <Route path="/corezoid" element={
              <ProtectedRoute>
                <CorezoidRoute />
              </ProtectedRoute>
            } />
            {/* Admin-only routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRoute />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/live" element={
              <AdminRoute>
                <LiveDashboard />
              </AdminRoute>
            } />
            <Route path="/gateways" element={
              <AdminRoute>
                <AdminLayout><AdminGateways /></AdminLayout>
              </AdminRoute>
            } />
            <Route path="/traders" element={
              <AdminRoute>
                <AdminLayout><Traders /></AdminLayout>
              </AdminRoute>
            } />
            <Route path="/prices" element={
              <AdminRoute>
                <AdminLayout><AdminPrices /></AdminLayout>
              </AdminRoute>
            } />
            <Route path="/banks" element={
              <AdminRoute>
                <AdminLayout><AdminBanks /></AdminLayout>
              </AdminRoute>
            } />
            <Route path="/currencies" element={
              <AdminRoute>
                <AdminLayout><AdminCurrencies /></AdminLayout>
              </AdminRoute>
            } />
            <Route path="/requests" element={
              <AdminRoute>
                <AdminLayout><AdminRequests /></AdminLayout>
              </AdminRoute>
            } />
            <Route path="/balance-operations" element={
              <AdminRoute>
                <AdminLayout><BalanceOperations /></AdminLayout>
              </AdminRoute>
            } />
            {/* Keep old bookmarks working while exposing only clean URLs. */}
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/:section" element={<LegacyAdminRedirect />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
